import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { t } from 'ttag'
import { useHistory } from 'react-router'
import { Box, Checkbox, FormControlLabel, Grid, useMediaQuery, useTheme } from '@material-ui/core'
import Hidden from '@material-ui/core/Hidden'
import { MRT_ColumnDef, MRT_FullScreenToggleButton } from 'material-react-table'
import { Card } from '../../components/Card/Card'
import NotificationsWrapper from '../../components/Notification/NotificationsWrapper'
import YumTable from '../../components/YumTable/YumTable'
import YumButton from '../../components/YumButton/YumButton'
import YumCopyButton from '../../components/YumCopyButton/YumCopyButton'
import YumSearchField from '../../components/YumSearchField/YumSearchField'
import { CompanyListItem } from './store/SuperAdminReducer'
import { isRtl } from '../../i18nInit'
import '../../views/SignUpCodeAdmin/components/SignUpCodeStatus/SignUpCodeStatus.scss'

const CLIENT_STATUS_COLORS: Record<string, string> = {
  DEMO_BOOKED: '#3170F9',
  DEMO_CANCELED: '#D7263D',
  DID_NOT_SHOW_UP: '#D7263D',
  SHOWED_UP: '#4caf50',
  NEGOTIATIONS: '#F59E0B',
  CONTRACT_DONE: '#3170F9',
  ACTIVE_CLIENT: '#4caf50',
  PAST_CLIENT: '#9D00A8',
  OTHER: '#F59E0B',
  LOST_LEAD: '#D7263D',
  LOST_COMPETITOR: '#D7263D',
  LOST_PRICE: '#D7263D',
  LOST_NOT_RESPONSIVE: '#D7263D',
  LOST_CONTACT_LEFT: '#D7263D',
  LOST_PRODUCT: '#D7263D',
  LOST_TIMING: '#D7263D',
  LOST_INTERNAL_CHANGE: '#D7263D',
  LOST_OTHER: '#D7263D',
}

function ClientStatusBadge({ status, userCreated }: { status: string; userCreated: boolean }) {
  const label = userCreated ? t`User created` : status || '-'
  if (!userCreated && !status) return null
  const color = CLIENT_STATUS_COLORS[status] || '#9E9E9E'
  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        padding: '6px 13px',
        borderRadius: '1rem',
        fontSize: 14,
        lineHeight: '16px',
        color,
        backgroundColor: `${color}1A`,
      }}
    >
      <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: color, flexShrink: 0 }} />
      {label}
    </div>
  )
}

export interface ISuperAdminStateProps {
  searchResults: CompanyListItem[]
  searchLoading: boolean
  totalCount: number
  page: number
  targetCompany: CompanyListItem | null
}

export interface ISuperAdminDispatchProps {
  searchCompanies: (payload: { query: string; page: number; pageSize: number; includeUserCreated: boolean }) => void
  setSuperAdminTargetCompany: (company: CompanyListItem) => void
  clearSuperAdminTargetCompany: () => void
}

type ISuperAdminProps = ISuperAdminStateProps & ISuperAdminDispatchProps

export default function SuperAdmin(props: ISuperAdminProps) {
  const { searchResults, searchLoading, totalCount, searchCompanies, setSuperAdminTargetCompany } = props
  const history = useHistory()
  const [searchQuery, setSearchQuery] = useState('')
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 })
  const [includeUserCreated, setIncludeUserCreated] = useState(false)
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'))

  useEffect(() => {
    searchCompanies({
      query: searchQuery,
      page: pagination.pageIndex,
      pageSize: pagination.pageSize,
      includeUserCreated,
    })
  }, [searchQuery, pagination.pageIndex, pagination.pageSize, includeUserCreated])

  useEffect(() => {
    setPagination((prev) => ({ ...prev, pageIndex: 0 }))
  }, [searchQuery, includeUserCreated])

  const handleManage = useCallback(
    (company: CompanyListItem) => {
      setSuperAdminTargetCompany(company)
      history.push('/admin/users')
    },
    [setSuperAdminTargetCompany, history],
  )

  const columns = useMemo<MRT_ColumnDef<CompanyListItem>[]>(
    () => [
      {
        accessorKey: 'companyId',
        header: 'ID',
        enableHiding: true,
      },
      {
        accessorKey: 'companyName',
        header: t`Company`,
        size: 300,
        Cell: ({ row }: any) => {
          const { companyId, companyName } = row.original
          return (
            <div style={{ lineHeight: 1.3 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <span>{companyName}</span>
                <YumCopyButton text={companyName} size={14} />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, opacity: 0.5, fontSize: '0.75em' }}>
                <span>{companyId}</span>
                <YumCopyButton text={companyId} size={12} />
              </div>
            </div>
          )
        },
      },
      {
        accessorKey: 'clientStatus',
        header: t`Client status`,
        size: 140,
        Cell: ({ row }: any) => (
          <ClientStatusBadge status={row.original.clientStatus} userCreated={row.original.userCreated} />
        ),
      },
      {
        accessorKey: 'users',
        header: t`Users`,
        size: 80,
      },
      {
        accessorKey: 'employees',
        header: t`Employees`,
        size: 100,
      },
      {
        accessorKey: 'active',
        header: t`Active`,
        size: 80,
        Cell: ({ row }: any) => {
          const active = row.original.active
          const status = active ? 'active' : 'expired'
          return (
            <div className={`sign-up-code-status sign-up-code-status__${status}`}>
              <span className="sign-up-code-status-dot"></span>
              {active ? t`Active` : t`Inactive`}
            </div>
          )
        },
      },
      {
        accessorKey: 'plan',
        header: t`Plan`,
        size: 120,
      },
      {
        id: 'contract',
        header: t`Contract period`,
        size: 200,
        Cell: ({ row }: any) => {
          const { startAt, paidUntil } = row.original
          return startAt && paidUntil ? `${startAt} – ${paidUntil}` : '-'
        },
      },
      {
        accessorKey: 'createdAt',
        header: t`Created`,
        size: 120,
        Cell: ({ row }: any) => {
          const { createdAt } = row.original
          return createdAt ? createdAt.substring(0, 16).replace('T', ' ') : '-'
        },
      },
      {
        id: 'manage',
        header: '',
        size: 100,
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ row }: any) => (
          <YumButton variant="contained" color="primary" size="small" onClick={() => handleManage(row.original)}>
            {t`Manage`}
          </YumButton>
        ),
      },
    ],
    [handleManage],
  )

  return (
    <Card>
      <NotificationsWrapper />

      <Grid
        container
        style={{ marginBottom: '1rem' }}
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        wrap="nowrap"
      >
        <Grid item style={{ flexShrink: '0', paddingRight: isRtl ? 0 : '10px', paddingLeft: isRtl ? '10px' : 0 }}>
          <h1 style={{ marginBottom: 3 }}>
            {t`Super admin`}
            {!searchLoading && <span style={{ opacity: 0.5, whiteSpace: 'nowrap' }}> ({totalCount})</span>}
          </h1>
        </Grid>
      </Grid>

      <div>
        <YumTable
          columns={columns as any}
          getRowId={(row: any) => row.companyId}
          data={searchResults}
          enableRowNumbers={false}
          enableGlobalFilter={false}
          manualPagination
          rowCount={totalCount}
          onPaginationChange={setPagination}
          state={{
            isLoading: searchLoading,
            pagination,
          }}
          initialState={{
            sorting: [{ id: 'createdAt', desc: true }],
            density: 'compact',
            columnVisibility: { companyId: false },
            columnPinning: { right: ['manage'] },
            showGlobalFilter: false,
          }}
          renderTopToolbar={({ table }) => (
            <Box style={{ padding: table.getState().isFullScreen ? '8px' : '0px' }}>
              <Box style={{ display: 'flex', alignItems: 'center' }}>
                <Box style={{ flex: 1 }}>
                  <YumSearchField
                    value={searchQuery}
                    onChange={setSearchQuery}
                    autoFocus={false}
                    debounceTime={400}
                    style={isDesktop ? (isRtl ? { marginLeft: '10px' } : { marginRight: '10px' }) : undefined}
                  />
                </Box>
                <Hidden smDown>
                  <MRT_FullScreenToggleButton
                    table={table}
                    onClick={() => {
                      const { isFullScreen } = table.getState()
                      table.setIsFullScreen(!isFullScreen)
                      if (!isFullScreen) {
                        document.querySelectorAll<HTMLElement>('.nav')[0].style.zIndex = '0'
                        document.querySelectorAll<HTMLElement>('.layout__header')[0].style.zIndex = '0'
                      } else {
                        document.querySelectorAll<HTMLElement>('.nav')[0].style.zIndex = '10'
                        document.querySelectorAll<HTMLElement>('.layout__header')[0].style.zIndex = '100'
                      }
                    }}
                  />
                </Hidden>
              </Box>
              <Box style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '24px' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={includeUserCreated}
                      onChange={() => setIncludeUserCreated((v) => !v)}
                      color="primary"
                      size="small"
                    />
                  }
                  label={t`Include user created`}
                  style={{ marginRight: 0 }}
                />
              </Box>
            </Box>
          )}
        />
      </div>
    </Card>
  )
}
